import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiTrash2, FiMinus, FiPlus, FiArrowLeft, FiShoppingBag } from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';

const Cart = () => {
  const { state, dispatch, totalPrice } = useCart();
  const { addToast } = useToast();

  const handleRemove = (id, name) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: id });
    addToast(`${name} removed from cart`, 'error');
  };

  const handleQuantity = (id, qty) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity: qty } });
  };

  const shipping = totalPrice > 9900 ? 0 : 990;
  const tax = totalPrice * 0.08;
  const total = totalPrice + shipping + tax;

  if (state.items.length === 0) {
    return (
      <div className="cart-empty-page">
        <div className="container">
          <motion.div
            className="empty-cart-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="empty-icon-big">🛒</div>
            <h1>Your Cart is Empty</h1>
            <p>Looks like you haven't added anything yet. Start shopping!</p>
            <Link to="/shop" className="btn btn-primary btn-lg">
              <FiShoppingBag /> Start Shopping
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <div className="page-header">
          <Link to="/shop" className="btn btn-ghost">
            <FiArrowLeft /> Continue Shopping
          </Link>
          <h1 className="page-title">Shopping Cart</h1>
          <span className="cart-item-count">{state.items.length} items</span>
        </div>

        <div className="cart-layout">
          {/* Items */}
          <div className="cart-items-list">
            {state.items.map(item => (
              <motion.div
                key={item.id}
                className="cart-row"
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <img src={item.image} alt={item.name} className="cart-row-img" />
                <div className="cart-row-info">
                  <h3 className="cart-row-name">{item.name}</h3>
                  <span className="cart-row-cat">{item.category}</span>
                </div>
                <div className="cart-row-qty">
                  <button className="qty-btn" onClick={() => handleQuantity(item.id, item.quantity - 1)}>
                    <FiMinus size={14} />
                  </button>
                  <span>{item.quantity}</span>
                  <button className="qty-btn" onClick={() => handleQuantity(item.id, item.quantity + 1)}>
                    <FiPlus size={14} />
                  </button>
                </div>
                <div className="cart-row-price">
                  <span className="cart-row-total">¥{(item.price * item.quantity).toFixed(2)}</span>
                  <span className="cart-row-unit">¥{item.price} each</span>
                </div>
                <button className="cart-remove-btn" onClick={() => handleRemove(item.id, item.name)}>
                  <FiTrash2 size={16} />
                </button>
              </motion.div>
            ))}
          </div>

          {/* Summary */}
          <div className="cart-summary">
            <h2 className="summary-title">Order Summary</h2>
            <div className="summary-lines">
              <div className="summary-line">
                <span>Subtotal</span>
                <span>¥{totalPrice.toFixed(2)}</span>
              </div>
              <div className="summary-line">
                <span>Shipping</span>
                <span>{shipping === 0 ? <span className="free">FREE</span> : `¥${shipping}`}</span>
              </div>
              <div className="summary-line">
                <span>Tax (8%)</span>
                <span>¥{tax.toFixed(2)}</span>
              </div>
              <div className="summary-divider" />
              <div className="summary-line summary-total-line">
                <span>Total</span>
                <span>¥{total.toFixed(2)}</span>
              </div>
            </div>

            {shipping > 0 && (
              <div className="free-shipping-msg">
                Add ¥{(9900 - totalPrice).toFixed(0)} more for <strong>free shipping</strong>!
              </div>
            )}

            <Link to="/checkout" className="btn btn-primary btn-full btn-lg">
              Proceed to Checkout
            </Link>

            <div className="payment-methods">
              <span>We accept:</span>
              <div className="payment-icons-row">
                {['💳 Visa', '💳 MC', '🏦 Bank', 'PayPal'].map(p => (
                  <span key={p} className="payment-chip">{p}</span>
                ))}
              </div>
            </div>

            <div className="promo-code-section">
              <h4>Promo Code</h4>
              <div className="promo-input-row">
                <input type="text" placeholder="Enter code" className="promo-input" />
                <button className="btn btn-outline">Apply</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
